import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect, useRef } from "react";
import {
  Bold, Italic, List, ListOrdered, Heading2, Link as LinkIcon, Pilcrow, Eraser, Undo2, Redo2,
} from "lucide-react";

// Minimal WYSIWYG editor for the bulk-email body. Outputs HTML that the
// backend forwards untouched to recipients (the bulk-email send path detects
// real HTML and skips its plain-text → HTML conversion).
//
// Props:
//   value     — current HTML string
//   onChange  — (html) => void
//   placeholder — optional placeholder text
//   ariaLabel — optional accessible label
export default function RichTextEditor({ value = "", onChange, placeholder, ariaLabel = "Email body" }) {
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          style: "color:#D4A84B;",
        },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        // The actual contenteditable. Inline style guarantees min-height
        // regardless of the host project's Tailwind config (no `prose`
        // plugin or arbitrary-value support required).
        class: "rte-content",
        style: "min-height:220px; padding:14px 14px 16px; outline:none; color:#e4e4e7; line-height:1.55; font-size:14px;",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap returns "<p></p>" for empty editor — normalise to "" so
      // form-validation and history empty-checks behave naturally.
      onChangeRef.current?.(html === "<p></p>" ? "" : html);
    },
  });

  // External value changes (e.g. switching language tab, AI translation)
  // should sync into the editor without losing user focus when the value
  // already matches what's there.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={
        "p-1.5 rounded text-sm transition-colors " +
        (active
          ? "bg-burns-red/30 text-white"
          : "text-zinc-400 hover:bg-zinc-800 hover:text-white")
      }
    >
      {children}
    </button>
  );

  const handleSetLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove):", prev);
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    let href = url.trim();
    if (!/^[a-z]+:/i.test(href) && !href.startsWith("mailto:")) {
      href = "https://" + href;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <div className="rounded-md border border-white/20 bg-zinc-950 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-white/10 bg-zinc-900 px-2 py-1">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl/Cmd+B)"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl/Cmd+I)"><Italic className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraph"><Pilcrow className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={handleSetLink} active={editor.isActive("link")} title="Link"><LinkIcon className="w-4 h-4" /></Btn>
        <Btn
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
        >
          <Eraser className="w-4 h-4" />
        </Btn>
        <span className="ml-auto flex items-center gap-1">
          <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo2 className="w-4 h-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo2 className="w-4 h-4" /></Btn>
        </span>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {!editor.getText() && placeholder && (
          <p
            className="pointer-events-none absolute top-3.5 left-4 text-zinc-600 text-sm select-none"
            aria-hidden="true"
          >
            {placeholder}
          </p>
        )}
      </div>

      {/* Inline styles for the editable area — keeps the editor visually
          consistent without depending on @tailwindcss/typography. */}
      <style>{`
        .rte-content p { margin: 0 0 10px; }
        .rte-content p:last-child { margin-bottom: 0; }
        .rte-content ul { list-style: disc; padding-left: 22px; margin: 8px 0; }
        .rte-content ol { list-style: decimal; padding-left: 22px; margin: 8px 0; }
        .rte-content li { margin: 2px 0; }
        .rte-content h2 { font-size: 18px; font-weight: 700; margin: 14px 0 8px; color: #fff; }
        .rte-content h3 { font-size: 16px; font-weight: 700; margin: 10px 0 6px; color: #fff; }
        .rte-content a { color: #D4A84B; text-decoration: underline; }
        .rte-content strong { color: #fff; font-weight: 700; }
      `}</style>
    </div>
  );
}
